import { AxiosError } from 'axios';
import { Editor, EventInfo } from 'ckeditor5';
import { documentApi } from '../../api/document';
import {
  createQualioDocumentPatchForSectionUpdate,
  QualioDocument,
  QualioDocumentSection,
  QualioDocumentSectionPatch,
} from '../../api/model/document';
import {
  DOCUMENT_REDUCER_SAVE_ERROR,
  logCustomError,
} from '../../messages/LogErrorMessages';
import {
  savingFailedCloudSectionVersion,
  savingFailedErrorMessage,
} from '../../messages/UserErrorMessages';
import {
  addRootToChangedRootsList,
  addSectionToEditedSectionsList,
  clearChangedDocumentRoots,
  snapshotChangedDocumentRoots,
} from '../../util/ActivityLogUtils';
import { getDocumentEditorGlobalWindowObject } from '../../util/AppUtils';
import { CKEditorUtils } from '../../util/CKEditorUtils';
import { isCommentsOnlyMode } from '../../util/EditModeUtils';
import { getEditorVersionEnvironmentVariable } from '../../util/EnvUtils';
import { parseHTMLString } from '../../util/HTMLElementUtils';

type SaveErrorHandler = (
  msg: string,
  sectionEditor?: Editor,
  doc?: QualioDocument,
) => void;

const buildUpdatedSectionPatches = (
  document: QualioDocument,
  sections: [string, string][],
  changedRoots: Set<string>,
  cloudSectionVersion: number | undefined,
  isCommentOrReadOnlyEditor: boolean,
  isTrackChangesModeEnabled: boolean | undefined,
): {
  updatedSections: QualioDocumentSectionPatch[];
  shouldCreateSectionEditActivityLog: boolean;
} => {
  const updatedSections: QualioDocumentSectionPatch[] = [];
  let shouldCreateSectionEditActivityLog = false;
  for (const [id, content] of sections) {
    if (!changedRoots.has(id)) continue;
    if (id === 'table-of-contents') continue;
    const sectionId = Number(id);
    const existing = document.sections.find((s) => s.id === sectionId);
    if (!existing) {
      throw new Error('Could not find section with id ' + sectionId);
    }
    if (
      !isCommentOrReadOnlyEditor &&
      !isTrackChangesModeEnabled &&
      addSectionToEditedSectionsList(sectionId, 'document')
    ) {
      shouldCreateSectionEditActivityLog = true;
    }
    updatedSections.push(
      createSectionPatch(existing, content, cloudSectionVersion),
    );
  }
  return { updatedSections, shouldCreateSectionEditActivityLog };
};

const buildBackfillSectionPatches = (
  document: QualioDocument,
  sections: [string, string][],
  changedRoots: Set<string>,
  cloudSectionVersion: number | undefined,
): QualioDocumentSectionPatch[] => {
  const backfillSections: QualioDocumentSectionPatch[] = [];
  for (const [id, content] of sections) {
    if (changedRoots.has(id)) continue;
    const existing = document.sections.find((s) => s.id === Number(id));
    if (!existing) continue;
    if (!hasUnpersistedHeadingIds(existing.content, content)) continue;
    backfillSections.push(
      createSectionPatch(existing, content, cloudSectionVersion),
    );
  }
  return backfillSections;
};

const buildFallbackSectionPatches = (
  document: QualioDocument,
  documentData: { [key: number]: string },
  cloudSectionVersion: number | undefined,
): QualioDocumentSectionPatch[] => {
  const updatedSections: QualioDocumentSectionPatch[] = [];
  for (const section of document.sections) {
    const content = documentData[section.id];
    if (content === undefined) continue;
    updatedSections.push(
      createSectionPatch(section, content, cloudSectionVersion),
    );
  }
  return updatedSections;
};

interface PersistDocumentUpdateOptions {
  payload: any;
  document: QualioDocument;
  editor: Editor;
  shouldCreateSectionEditActivityLog: boolean;
  updateLastModifiedTimestamp: boolean;
  backfillSections: QualioDocumentSectionPatch[];
  changedRoots: Set<string> | null;
  handleSaveError: SaveErrorHandler;
  handleSaveSuccess: () => void;
}

const persistDocumentUpdate = async ({
  payload,
  document,
  editor,
  shouldCreateSectionEditActivityLog,
  updateLastModifiedTimestamp,
  backfillSections,
  changedRoots,
  handleSaveError,
  handleSaveSuccess,
}: PersistDocumentUpdateOptions) => {
  try {
    if (!updateLastModifiedTimestamp) {
      await documentApi.update(
        payload,
        document.id,
        shouldCreateSectionEditActivityLog,
        false,
      );
    } else {
      await documentApi.update(
        payload,
        document.id,
        shouldCreateSectionEditActivityLog,
      );
      getDocumentEditorGlobalWindowObject().lastUpdated =
        new Date().toISOString();
    }

    if (backfillSections.length > 0) {
      await documentApi.update(
        { ...payload, sections: backfillSections },
        document.id,
        false,
        false,
      );
    }

    if (changedRoots) {
      clearChangedDocumentRoots(changedRoots);
    }
    handleSaveSuccess();
  } catch (error: any) {
    if (errorIsCloudDocumentVersionRejection(error)) {
      handleSaveError(savingFailedCloudSectionVersion(''));
      return;
    }
    logCustomError(DOCUMENT_REDUCER_SAVE_ERROR, {
      error,
      documentId: document.id,
    });
    handleSaveError(
      error.response?.data?.message || savingFailedErrorMessage,
      editor,
      document,
    );
  }
};

export const saveContentFromMultiRootEditor = async (
  document: QualioDocument,
  documentData: {
    [key: number]: string;
  },
  editor: Editor,
  handleSaveError: SaveErrorHandler,
  handleSaveSuccess: () => void,
) => {
  const cloudSectionVersion = CKEditorUtils.getCloudDocumentVersion(editor);
  const isCommentOrReadOnlyEditor =
    isCommentsOnlyMode(editor) || editor.isReadOnly;
  const trackChangesCommand = editor.commands.get('trackChanges');
  const isTrackChangesModeEnabled =
    trackChangesCommand && trackChangesCommand.value;
  const updateLastModifiedTimestamp = !(
    isCommentOrReadOnlyEditor ||
    isTrackChangesModeEnabled ||
    document.status_id !== 'draft'
  );
  const sections = Object.entries(documentData);
  const tableOfContents = sections.find(
    (section) => section[0] === 'table-of-contents',
  );

  let changedRoots: Set<string> | null = null;
  let updatedSections: QualioDocumentSectionPatch[] = [];
  let backfillSections: QualioDocumentSectionPatch[] = [];
  let shouldCreateSectionEditActivityLog = false;

  try {
    changedRoots = snapshotChangedDocumentRoots();
    const isTableOfContentsChanged = changedRoots.has('table-of-contents');

    const built = buildUpdatedSectionPatches(
      document,
      sections,
      changedRoots,
      cloudSectionVersion,
      isCommentOrReadOnlyEditor,
      isTrackChangesModeEnabled,
    );
    updatedSections = built.updatedSections;
    shouldCreateSectionEditActivityLog =
      built.shouldCreateSectionEditActivityLog;

    if (isTableOfContentsChanged) {
      backfillSections = buildBackfillSectionPatches(
        document,
        sections,
        changedRoots,
        cloudSectionVersion,
      );
    }

    if (updatedSections.length === 0 && !isTableOfContentsChanged) {
      throw new Error(
        'No sections changed, something has not been caught by the snapshotChangedDocumentRoots() check. This should not happen. ',
      );
    }
  } catch (error) {
    // Fall back: log error, send all known sections, skip activity log
    console.warn(error);
    changedRoots = null;
    shouldCreateSectionEditActivityLog = false;
    backfillSections = [];
    updatedSections = buildFallbackSectionPatches(
      document,
      documentData,
      cloudSectionVersion,
    );
  }

  if (!shouldCreateSectionEditActivityLog) {
    updatedSections.push(...backfillSections);
    backfillSections = [];
  }

  const payload = {
    editor_version: getEditorVersionEnvironmentVariable(true),
    sections: updatedSections,
    content_last_modified_at: updateLastModifiedTimestamp
      ? Date.now() / 1000
      : document.content_last_modified_at,
    table_of_contents: tableOfContents ? tableOfContents[1] : null,
  };

  await persistDocumentUpdate({
    payload,
    document,
    editor,
    shouldCreateSectionEditActivityLog,
    updateLastModifiedTimestamp,
    backfillSections,
    changedRoots,
    handleSaveError,
    handleSaveSuccess,
  });
};

export const saveSectionFromEditor = async (
  document: QualioDocument,
  sectionEditor: Editor,
  handleSaveError: (
    msg: string,
    sectionEditor?: Editor,
    doc?: QualioDocument,
  ) => void,
  handleSaveSuccess: () => void,
) => {
  // only updates to smart link texts can make content in an for_approval document change. We do not need to patch this update to MTBE as every fetch of the doc from MTBE will update the smart link text on the fly anyways
  if (document.status_id !== 'draft' && document.status_id !== 'for_review') {
    return;
  }
  const sectionText = sectionEditor.getData();
  if (sectionText !== undefined) {
    const sectionIdToUpdate = sectionEditor.config.get('sectionId') as number;
    const cloudSectionVersion =
      CKEditorUtils.getCloudDocumentVersion(sectionEditor);
    const isCommentOrReadOnlyEditor =
      isCommentsOnlyMode(sectionEditor) || sectionEditor.isReadOnly;
    const trackChangesCommand = sectionEditor.commands.get('trackChanges');
    const isTrackChangesModeEnabled =
      trackChangesCommand && trackChangesCommand.value;
    let updateLastModifiedTimestamp = true;
    if (
      isCommentOrReadOnlyEditor ||
      isTrackChangesModeEnabled ||
      document.status_id !== 'draft'
    ) {
      updateLastModifiedTimestamp = false;
    }

    const updatedSection = document.sections.find(
      (section) => section.id === sectionIdToUpdate,
    );

    const shouldCreateSectionEditActivityLog =
      !isCommentOrReadOnlyEditor && !isTrackChangesModeEnabled
        ? addSectionToEditedSectionsList(sectionIdToUpdate, 'document')
        : false;

    const payload = createQualioDocumentPatchForSectionUpdate(
      document,
      updatedSection,
      sectionText,
      cloudSectionVersion,
      getEditorVersionEnvironmentVariable(),
      updateLastModifiedTimestamp,
    );

    try {
      if (!updateLastModifiedTimestamp) {
        await documentApi.update(
          payload,
          document.id,
          shouldCreateSectionEditActivityLog,
          false,
        );
      } else {
        await documentApi.update(
          payload,
          document.id,
          shouldCreateSectionEditActivityLog,
        );
        getDocumentEditorGlobalWindowObject().lastUpdated =
          new Date().toISOString();
      }

      handleSaveSuccess();
    } catch (error: any) {
      if (errorIsCloudDocumentVersionRejection(error)) {
        handleSaveError(
          savingFailedCloudSectionVersion(updatedSection?.position || ''),
        );
        return;
      }
      logCustomError(DOCUMENT_REDUCER_SAVE_ERROR, {
        error,
        documentId: document.id,
      });
      handleSaveError(
        error.response?.data?.message || savingFailedErrorMessage,
        sectionEditor,
        document,
      );
    }
  }
};

const createSectionPatch = (
  section: QualioDocumentSection,
  content: string,
  cloudSectionVersion: number | undefined,
): QualioDocumentSectionPatch => ({
  id: section.id,
  content,
  cloud_section_version: cloudSectionVersion,
  title: section.title,
  type: section.type,
});

const getHeadingIds = (content: string): string[] =>
  Array.from(
    parseHTMLString(content).querySelectorAll(
      'h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]',
    ),
  ).map((heading) => heading.id);

const hasUnpersistedHeadingIds = (
  persistedContent: string,
  currentContent: string,
): boolean => {
  const persistedIds = new Set(getHeadingIds(persistedContent));
  return getHeadingIds(currentContent).some((id) => !persistedIds.has(id));
};

const errorIsCloudDocumentVersionRejection = (err: any) => {
  if (err instanceof AxiosError) {
    if (err.response?.status === 400) {
      const msg = err.response?.data?.cloud_section_version || '';
      if (/cloud_section_version \d+ out of date/.test(msg)) {
        return true;
      }
    }
  }
  return false;
};

export function configureChangedRootsTracker(editor: Editor) {
  editor.model.document.on('change:data', (_evt: EventInfo, batch: any) => {
    if (!batch.isLocal) return;
    for (const operation of batch.operations) {
      for (const rootName of getOperationRootNames(operation)) {
        if (
          rootName &&
          !rootName.includes('header') &&
          !rootName.startsWith('$')
        ) {
          addRootToChangedRootsList(rootName);
        }
      }
    }
  });
}

const getOperationRootNames = (operation: any): Array<string | undefined> => {
  return [
    operation.position?.root?.rootName,
    operation.sourcePosition?.root?.rootName,
    operation.targetPosition?.root?.rootName,
    operation.deletionPosition?.root?.rootName,
    operation.moveTargetPosition?.root?.rootName,
    operation.range?.start?.root?.rootName,
    operation.oldRange?.start?.root?.rootName,
    operation.movedRange?.start?.root?.rootName,
    operation.newRange?.start?.root?.rootName,
    operation.root?.rootName,
    operation.rootName,
  ];
};
